import { useEffect, useRef } from "react";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import Button from "@mui/material/Button";

const speak = (text) => {
    try {
        if (typeof window !== "undefined" && window.speechSynthesis) {
            const u = new SpeechSynthesisUtterance(text);
            window.speechSynthesis.speak(u);
        }
    } catch (e) {}
};

/**
 * Realtime hook for Production Center orders.
 *
 * @param {object} params
 * @param {any} params.socket - socket.io instance from AppContext
 * @param {Array} params.orders - current orders array to snapshot before refetch
 * @param {Function} params.refetchOrders - react-query refetch function returning a promise with { data }
 * @param {Function} params.onExpandOrder - callback(orderName) to expand/focus a specific order
 */
export const useProductionCenterRealtime = ({ socket, orders, refetchOrders, onExpandOrder, onOrdersAdded }) => {
    const prevOrderNamesRef = useRef(new Set());

    useEffect(() => {
        if (!socket) return;

        const handleListUpdate = () => {
            // Snapshot current orders before refetch
            const prev = new Set((orders || []).map((o) => o?.name).filter(Boolean));
            prevOrderNamesRef.current = prev;

            refetchOrders?.().then((res) => {
                const data = res?.data || res;
                const nextOrders = Array.isArray(data) ? data : (Array.isArray(data?.message) ? data.message : []);
                const added = [];
                (nextOrders || []).forEach((o) => {
                    if (o?.name && !prevOrderNamesRef.current.has(o.name)) {
                        added.push(o.name);
                    }
                });

                if (added.length > 0) {
                    // Voice + toast for each new order
                    speak("New order added");
                    onOrdersAdded?.(added);
                    added.forEach((name) => {
                        enqueueSnackbar(`New order ${name}`, {
                            variant: "info",
                            autoHideDuration: 5000,
                            action: (snackbarId) => (
                                <Button
                                    size="small"
                                    onClick={() => {
                                        closeSnackbar(snackbarId);
                                        onExpandOrder?.(name);
                                    }}
                                >
                                    Expand
                                </Button>
                            ),
                        });
                    });
                }
            });
        };

        const handleVoided = (msg) => {
            const orderName = msg?.order_name;
            if (orderName) {
                enqueueSnackbar(`Order ${orderName} voided`, { variant: "warning" });
            }
        };

        const handleEntryDeleted = (msg) => {
            const orderName = msg?.order_name;
            const itemCode = msg?.item_code;
            const qty = msg?.qty;
            if (orderName && itemCode) {
                enqueueSnackbar(`Deleted ${qty || ""} ${itemCode} from ${orderName}`, {
                    variant: "warning",
                });
            }
        };

        socket.on("list_update", handleListUpdate);
        socket.on("table_order_voided", handleVoided);
        socket.on("entry_item_deleted", handleEntryDeleted);

        return () => {
            socket.off("list_update", handleListUpdate);
            socket.off("table_order_voided", handleVoided);
            socket.off("entry_item_deleted", handleEntryDeleted);
        };
    }, [socket, orders, refetchOrders, onExpandOrder]);
};

export default useProductionCenterRealtime;


